import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import type { BBCommonProps } from 'types/story';
import { COMPONENT_STATES } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import Loader from 'common/components/Loader';
import { useClientStoryDictionary } from 'common/i18n/hooks/useClientStoryDictionary';
import { IS_PROD } from 'src/common/utils/environment';
import { ErrorMessage } from '../Error';
import { withField, WithFieldT } from '../utils';
import { FIELD_DATA_ATTR, FieldBBErrorType, FieldBBType } from '../constants';
import css from './FileUploadField.scss';

type Props = BBCommonProps & WithFieldT;

const UploadIcon = () => (
	<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="17 8 12 3 7 8" />
		<line x1="12" y1="3" x2="12" y2="15" />
	</svg>
);

const RemoveIcon = () => (
	<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

function parseAccept(allowStr: string | undefined): string {
	if (!allowStr || typeof allowStr !== 'string') return '*';
	const trimmed = allowStr.trim();
	if (!trimmed) return '*';
	return trimmed
		.split(/[,;\s]+/)
		.map(ext => (ext.startsWith('.') ? ext : `.${ext}`))
		.join(',');
}

function getMaxSizeBytes(maxSizeMb: string | undefined): number {
	const number = parseFloat(maxSizeMb ?? '');
	return Number.isNaN(number) ? Infinity : number * 1024 * 1024;
}

type UploadedFile = { name: string; size: number; placeholderId: string };

function extensionAllowed(filename: string, allowStr: string): boolean {
	if (!allowStr || allowStr === '*') return true;
	const ext = filename.includes('.') ? `.${filename.split('.').pop()?.toLowerCase()}` : '';
	const allowed = allowStr.split(/[,;\s]+/).map(e => (e.startsWith('.') ? e.toLowerCase() : `.${e.toLowerCase()}`));
	return allowed.some(a => a === ext || a === '*');
}

const FileUploadField: React.FC<Props> = props => {
	const { dictionary } = useClientStoryDictionary();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [clientErrorType, setClientErrorType] = useState<FieldBBErrorType | null>(null);

	const { uiConfig, stateAttrs, eventListeners, containerRef, isEditableMode, formContext, getErrorMsg } = props;
	const { addToUploadQueue, removeFromUploadQueue } = formContext ?? {};
	const { name } = uiConfig.editorProps;
	const { fieldError, fieldPlaceholder, fileUploadAllowedTypes = '' } = uiConfig.componentProps;
	const maxSizeBytes = getMaxSizeBytes(uiConfig.componentProps.fileUploadMaxSizeMb);
	const { setStates, states } = props;
	const accept = parseAccept(fileUploadAllowedTypes);

	// TODO: remove this after testing
	useEffect(() => {
		if (!IS_PROD) {
			if (name === 'default') {
				// ...
			} else if (name === 'uploading') {
				setIsUploading(true);
			} else if (name === 'uploaded') {
				setUploadedFile({ name: 'uploaded.pdf', size: 1000000, placeholderId: 'placeholder-123' });
			}
		}
	}, [name]);

	const nodeProps = {
		...props.uiConfig.nodeProps,
		...(props.editableModeProps?.nodeProps ?? {}),
		className: cn(
			css.fileUploadField,
			props.uiConfig.nodeProps?.className,
			props.editableModeProps?.nodeProps?.className,
			{
				[css.uploading]: isUploading,
				[css.uploaded]: !!uploadedFile,
				[css.hasError]: states[COMPONENT_STATES.INCORRECT],
			}
		),
	};

	const isIncorrect = 'data-incorrect-state' in nodeProps || states[COMPONENT_STATES.INCORRECT] || !!clientErrorType;
	const errorMessage =
		clientErrorType && fieldError?.[clientErrorType] ? fieldError[clientErrorType] : getErrorMsg(nodeProps);

	const clearError = useCallback(() => {
		setClientErrorType(null);
		if (setStates) {
			setStates({ ...states, [COMPONENT_STATES.INCORRECT]: false, [COMPONENT_STATES.CORRECT]: true });
		}
	}, [setStates, states]);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];

			if (!file) return;

			if (file.size > maxSizeBytes) {
				setClientErrorType(FieldBBErrorType.fileSize);
				if (setStates) {
					setStates({
						...states,
						[COMPONENT_STATES.INCORRECT]: true,
						[COMPONENT_STATES.CORRECT]: false,
					});
				}
				return;
			}

			if (!extensionAllowed(file.name, fileUploadAllowedTypes)) {
				setClientErrorType(FieldBBErrorType.invalidFileType);
				if (setStates) {
					setStates({
						...states,
						[COMPONENT_STATES.INCORRECT]: true,
						[COMPONENT_STATES.CORRECT]: false,
					});
				}
				return;
			}

			setClientErrorType(null);
			setIsUploading(true);
			addToUploadQueue?.(nodeProps.id);
			if (setStates) {
				setStates({ ...states, [COMPONENT_STATES.INCORRECT]: false, [COMPONENT_STATES.CORRECT]: true });
			}

			setTimeout(() => {
				const placeholderId = `placeholder-${Date.now()}-${file.name}`;
				setUploadedFile({ name: file.name, size: file.size, placeholderId });
				removeFromUploadQueue?.(nodeProps.id);
				setIsUploading(false);
			}, 1000);
		},
		[maxSizeBytes, fileUploadAllowedTypes, setStates, states, addToUploadQueue, nodeProps.id, removeFromUploadQueue]
	);

	const handleRemove = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setUploadedFile(null);
			clearError();
		},
		[clearError]
	);

	const handleTriggerClick = useCallback(() => {
		if (isEditableMode) return;
		if (uploadedFile) return;
		fileInputRef.current?.click();
	}, [isEditableMode, uploadedFile]);

	const renderContent = () => {
		if (uploadedFile) {
			return (
				<>
					<span className={cn(css.fileName, css.textOverflow)} title={uploadedFile.name}>
						{uploadedFile.name}
					</span>
					<button
						type="button"
						className={css.removeBtn}
						onClick={handleRemove}
						disabled={isEditableMode}
						aria-label="Remove file"
					>
						<RemoveIcon />
					</button>
				</>
			);
		}

		if (isUploading) {
			return (
				<>
					<Loader size="small" className={css.spinner} color="currentColor" />
					<span className={cn(css.uploadingText, css.textOverflow)}>
						{dictionary?.FILE_UPLOAD_FIELD?.uploading ?? '...'}
					</span>
				</>
			);
		}

		return (
			<button className={css.uploadTrigger} type="button" onClick={handleTriggerClick} aria-label="Upload file">
				<span className={css.triggerLabel}>
					<span className={css.uploadIcon}>
						<UploadIcon />
					</span>
					{fieldPlaceholder && <span className={css.textOverflow}>{fieldPlaceholder}</span>}
				</span>
			</button>
		);
	};

	console.info('fileInputRef', fileInputRef.current?.value, fileInputRef.current?.files);

	return (
		<div {...stateAttrs} {...eventListeners} {...nodeProps} ref={containerRef as RefObject<HTMLDivElement>}>
			{!isEditableMode && (
				<>
					<input
						ref={fileInputRef}
						type="file"
						name={name}
						{...{ [FIELD_DATA_ATTR]: '' }}
						data-field-type={FieldBBType.file}
						accept={accept}
						className={css.fileInput}
						onChange={handleFileChange}
						tabIndex={-1}
						aria-hidden
					/>
				</>
			)}

			<div className={css.inner}>{renderContent()}</div>

			<ErrorMessage
				isHidden={!isIncorrect || !errorMessage}
				path={props.editableModeProps?.nodeProps?.['data-path']}
				isEditableMode={!!isEditableMode}
				errorType={uiConfig.editorProps[UNIFORM_PROPS.fieldErrorShow]}
				text={errorMessage}
				editorMode={props.editorMode}
			/>
		</div>
	);
};

export default withCardTransitionContext(withField(FileUploadField));
